// ---------------------------------------------------------------------------
// useSheetDismissDrag — arrastar a folha para baixo FECHA (BUG-214).
//
// Relato do fundador (05/09): *"tem alguns modais como o modal de ver outra
// liga que não fecha ao arrastar ele para baixo, só quando clica no X"*.
//
// 🔴 ISTO É A SEGUNDA METADE DO BUG-189, e a primeira metade está escrita no
// invariante 3 do `ux-mechanics`, com a medição junto: *"o handle é decorativo
// (não há swipe-to-dismiss), então ele convida a um gesto que o app não
// implementava e ainda repassava para o documento"*. Em 30/08 o repasse foi
// consertado (`lockAppScroll` + `touch-action`), e o convite ficou de pé: o
// puxador continua desenhado no topo de ~40 folhas, e continua não fazendo
// nada. Um affordance que não cumpre é pior que affordance nenhum — a pessoa
// conclui que a tela travou, que foi exatamente o sintoma relatado no BUG-189.
//
// ── As quatro decisões que este arquivo carrega ────────────────────────────
//
// ① **Listener NATIVO com `{ passive: false }`, nunca `onTouchMove` do React.**
//    O React registra `touchstart`/`touchmove` na raiz como PASSIVOS, então um
//    `e.preventDefault()` dentro do handler JSX é no-op (com aviso no console).
//    Sem `preventDefault` não há como impedir o navegador de rolar/fazer bounce
//    no meio do arrasto, e a folha andaria junto de um documento que também
//    anda.
//
// ② **`transform` inline com `!important`.** As classes de entrada e saída são
//    `animation: … both` (`theme.css`), e `animation-fill-mode: both` mantém um
//    valor da keyframe aplicado fora da janela da animação. Declaração de
//    animação vence `style=""` na cascata, então sem o `!important` o arrasto
//    calcula tudo certo e a folha NÃO SAI DO LUGAR.
//
//    Medido em Chromium (harness temporário, já removido, com a utility e a
//    keyframe copiadas verbatim do `theme.css`), sobre dois nós idênticos:
//    inline sem prioridade → computado `matrix(1,0,0,1,0,60)`, ou seja o valor
//    da ANIMAÇÃO; inline com `important` → `matrix(1,0,0,1,0,120)`, o valor
//    pedido. O que a medição prova é quem vence a cascata; a `120` é a única
//    que o arrasto consegue pintar.
//
// ③ **A decisão de assumir o gesto é tomada no PRIMEIRO movimento.** Depois que
//    o WebKit inicia um pan, `preventDefault` tardio é ignorado. Por isso o
//    hook decide em ~8px: se o dedo desceu, se desceu mais do que andou de
//    lado, e se o scroller sob o dedo já está no topo. Fora disso ele NÃO
//    reivindica — e a rolagem interna segue exatamente como antes.
//
// ④ **A saída é do hook, não da keyframe.** `animate-sheet-down` anima de
//    `translateY(0)`: reaproveitá-la faria a folha SALTAR de volta ao lugar
//    para só então descer. O `!important` do item ② também garante que ela não
//    interfira — a folha continua de onde o dedo a deixou.
//
// O gesto é de TOQUE apenas. Arrastar folha com o mouse não é gramática de
// desktop (e no desktop o `Modal` nem vira folha: vira overlay central), e
// pointer events trariam o risco de engolir cliques em toda a base.
// ---------------------------------------------------------------------------

import { useEffect, useRef, type RefObject } from "react";

/** Movimento até o hook decidir se o gesto é dele ou da rolagem. */
const CLAIM_PX = 8;

/**
 * Distância que fecha, em px.
 *
 * Fixa, e não uma fração da altura: a folha vai de ~180px (confirmação) a 90dvh
 * (a de listas), e uma fração faria o mesmo gesto significar coisas diferentes
 * em cada uma. 96px é ~1/4 do polegar médio de curso confortável e está acima
 * de qualquer arrasto acidental durante a rolagem.
 */
const DISMISS_PX = 96;

/** Arremesso: rápido o bastante fecha mesmo sem percorrer os 96px. */
const FLING_VELOCITY = 0.55; // px/ms
const FLING_MIN_PX = 24;

/**
 * Piso de tempo para a velocidade valer alguma coisa.
 *
 * 🔴 Sem ele, dois `touchmove` no MESMO milissegundo produzem uma velocidade
 * enorme e qualquer arrasto de 24px vira arremesso. Isso não é hipótese de
 * teste: quadro perdido, gesto sintético e a própria rajada de eventos que o
 * WebKit entrega ao retomar do background produzem amostras coladas. Abaixo de
 * meio quadro a medição não existe, e o que decide passa a ser só a distância.
 */
const MIN_SAMPLE_MS = 8;

/** Espelha `--motion-sheet` / `MOTION.sheet` — a saída e o unmount em sincronia. */
const EXIT_MS = 280;
const SPRING_MS = 200;

/**
 * O scroller sob o dedo, se houver, dentro dos limites do painel.
 *
 * Existe porque as folhas do app não têm uma forma só: umas rolam no painel,
 * outras num miolo dedicado, e algumas não rolam. Procurar o scroller a partir
 * do ALVO do toque é o que permite o mesmo hook servir as duas dúzias de
 * folhas escritas à mão sem que cada uma precise declarar um ref.
 */
function scrollerUnder(target: EventTarget | null, panel: HTMLElement): HTMLElement | null {
  let node = target instanceof Node ? target : null;
  while (node && node !== panel.parentNode) {
    if (node instanceof HTMLElement && node.scrollHeight > node.clientHeight) {
      const overflowY = getComputedStyle(node).overflowY;
      if (overflowY === "auto" || overflowY === "scroll") return node;
    }
    node = node.parentNode;
  }
  return null;
}

export interface SheetDismissDragOptions {
  /** O painel que se move. É nele que os listeners vivem. */
  panelRef: RefObject<HTMLElement | null>;
  /**
   * Liga o gesto. Desligue durante a saída, senão o arrasto disputa com ela.
   *
   * 🔴 **Precisa ser FALSO enquanto o painel não existe no DOM**, e isso não é
   * higiene: o efeito só reexecuta quando `enabled` muda, então um `enabled`
   * constante numa folha que se monta por dentro rodaria uma vez com o ref
   * nulo, e os listeners nunca seriam ligados — o gesto morre em silêncio, sem
   * erro nenhum. A maioria dos consumidores é montada e desmontada pelo PAI (o
   * mount já reexecuta o efeito) e não precisa pensar nisso; quem renderiza o
   * próprio portal precisa incluir o `open` aqui. Ver `DatePickerInput`.
   */
  enabled: boolean;
  /**
   * Chamado quando o gesto fecha. A folha JÁ está fora da tela quando ele roda,
   * então o consumidor deve fazer o que faria no ✕ — inclusive a saída dele,
   * que é inofensiva (o `!important` deste hook vence a keyframe).
   */
  onDismiss: () => void;
}

export function useSheetDismissDrag({ panelRef, enabled, onDismiss }: SheetDismissDragOptions) {
  /**
   * 🔴 Sobrevive à limpeza do efeito, e é isso que ele existe para fazer.
   *
   * Fechar pelo gesto chama `onDismiss`, que baixa o `enabled` — ou seja o
   * efeito é limpo NO MEIO da animação de saída. Uma limpeza que apagasse o
   * `transform` ali faria a folha SALTAR de volta ao lugar e só então descer
   * pela keyframe: o defeito que a decisão ④ do cabeçalho existe para evitar,
   * reintroduzido pela porta dos fundos.
   */
  const exitingRef = useRef(false);

  /**
   * O callback vive num ref e sai das dependências de propósito.
   *
   * Quase todo consumidor passa uma closure inline (`onDismiss={() => …}`), que
   * troca de identidade a cada render. Com ela na lista de dependências, os
   * listeners seriam removidos e recolocados a cada render — e um render no
   * meio do gesto zeraria o estado do arrasto, com a folha parando debaixo do
   * dedo. O ref mantém o alvo sempre atual sem remontar nada.
   */
  const dismissRef = useRef(onDismiss);
  // ⚠️ Num efeito, e não na fase de render: `react-hooks/refs` proíbe escrever
  // em ref durante o render, e a regra está certa aqui — o valor só é LIDO no
  // handler de toque, que roda muito depois do commit. Sem lista de
  // dependências de propósito: qualquer render deixa o alvo atualizado.
  useEffect(() => {
    dismissRef.current = onDismiss;
  });

  useEffect(() => {
    const panel = panelRef.current;
    if (!enabled || !panel) return;
    // Reabriu: a folha é a mesma instância de DOM em alguns consumidores, e um
    // `translateY` gravado na saída anterior a manteria fora da tela.
    exitingRef.current = false;
    panel.style.removeProperty("transform");
    panel.style.removeProperty("transition");

    let startY = 0;
    let startX = 0;
    let startAt = 0;
    let lastY = 0;
    let lastAt = 0;
    /** null = ainda decidindo; false = o gesto é da rolagem; true = é nosso. */
    let claimed: boolean | null = null;
    let scroller: HTMLElement | null = null;
    let cleanupTimer: ReturnType<typeof setTimeout> | undefined;

    /** `!important` é o ponto todo — ver a decisão ② no cabeçalho. */
    const move = (px: number) => {
      panel.style.setProperty("transform", `translateY(${px}px)`, "important");
    };

    const clearInline = () => {
      panel.style.removeProperty("transform");
      panel.style.removeProperty("transition");
    };

    const onStart = (e: TouchEvent) => {
      // Multitouch é pinça/zoom, não arrasto de folha.
      if (e.touches.length !== 1) {
        claimed = false;
        return;
      }
      clearTimeout(cleanupTimer);
      const t = e.touches[0];
      startY = lastY = t.clientY;
      startX = t.clientX;
      startAt = lastAt = e.timeStamp;
      claimed = null;
      scroller = scrollerUnder(e.target, panel);
      panel.style.removeProperty("transition");
    };

    const onMove = (e: TouchEvent) => {
      if (claimed === false || e.touches.length !== 1) return;
      const t = e.touches[0];
      const dy = t.clientY - startY;
      const dx = t.clientX - startX;
      lastY = t.clientY;
      lastAt = e.timeStamp;

      if (claimed === null) {
        if (Math.abs(dy) < CLAIM_PX && Math.abs(dx) < CLAIM_PX) return;
        // Três condições, e as três importam: para baixo, mais vertical que
        // horizontal (senão um carrossel dentro da folha vira arrasto), e com o
        // scroller sob o dedo já no topo — quem está no meio de uma lista quer
        // continuar rolando, não fechar.
        claimed = dy > 0 && Math.abs(dy) > Math.abs(dx) && (!scroller || scroller.scrollTop <= 0);
        if (!claimed) return;
      }

      // Para cima não abre nada: a folha já está no lugar dela. Prender em 0
      // (em vez de deixar subir) é o que impede o topo de descolar da borda.
      move(Math.max(0, dy));
      if (e.cancelable) e.preventDefault();
    };

    const onEnd = () => {
      if (!claimed) {
        claimed = null;
        return;
      }
      claimed = null;
      const dy = Math.max(0, lastY - startY);
      const elapsed = lastAt - startAt;
      const measurable = elapsed >= MIN_SAMPLE_MS;
      const velocity = measurable ? dy / elapsed : 0;
      const dismiss =
        dy >= DISMISS_PX || (measurable && velocity >= FLING_VELOCITY && dy >= FLING_MIN_PX);

      if (dismiss) {
        exitingRef.current = true;
        panel.style.setProperty("transition", `transform ${EXIT_MS}ms cubic-bezier(.4,0,1,1)`);
        // `getBoundingClientRect` devolve 0 fora de um navegador de verdade;
        // a queda para a altura da janela é o que impede a folha de "sair"
        // ficando exatamente onde está.
        move(panel.getBoundingClientRect().height || window.innerHeight);
        dismissRef.current();
        return;
      }

      // Volta ao lugar e LIMPA o inline. Deixar `translateY(0) !important`
      // gravado bloquearia a keyframe de saída no fechamento seguinte — a folha
      // sumiria de uma vez em vez de descer.
      panel.style.setProperty("transition", `transform ${SPRING_MS}ms cubic-bezier(.16,1,.3,1)`);
      move(0);
      cleanupTimer = setTimeout(clearInline, SPRING_MS + 20);
    };

    // `passive: false` no move é o que torna o `preventDefault` possível —
    // ver a decisão ① no cabeçalho.
    panel.addEventListener("touchstart", onStart, { passive: true });
    panel.addEventListener("touchmove", onMove, { passive: false });
    panel.addEventListener("touchend", onEnd);
    panel.addEventListener("touchcancel", onEnd);

    return () => {
      clearTimeout(cleanupTimer);
      panel.removeEventListener("touchstart", onStart);
      panel.removeEventListener("touchmove", onMove);
      panel.removeEventListener("touchend", onEnd);
      panel.removeEventListener("touchcancel", onEnd);
      if (!exitingRef.current) clearInline();
    };
  }, [enabled, panelRef]);
}
